import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  IconButton,
  CircularProgress,
  Pagination,
  InputAdornment,
  Stack,
  Chip,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Comment as CommentIcon, 
  Search as SearchIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../utils/axiosInstance';

interface Template {
  id: number;
  user_id: number;
  noidung: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface YKienMauPopupProps {
  open: boolean;
  onClose: () => void;
  onTemplateSelect?: (templateContent: string) => void; // Callback khi chọn template
}

const YKienMauPopup: React.FC<YKienMauPopupProps> = ({
  open,
  onClose,
  onTemplateSelect
}) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // States for form
  const [inputText, setInputText] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  
  // States for API data
  const [templates, setTemplates] = useState<Template[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // States for multi-select
  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  // Load templates from API
  const loadTemplates = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search })
      });
      
      const response = await axiosInstance.get(`/qlvb/incoming-documents/danhsach-template-ghichu?${params}`);
      
      if (response.data.code === 0) {
        setTemplates(response.data.data.templates);
        setPagination(response.data.data.pagination);
      } else {
        enqueueSnackbar(response.data.message || 'Có lỗi khi tải danh sách template', { variant: 'error' });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Có lỗi khi tải danh sách template';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load templates when popup opens
  useEffect(() => {
    if (open) {
      loadTemplates(1, '');
      setSearchText('');
      setInputText('');
      setEditingTemplate(null);
      setShowActionButtons(false);
      setEditingTemplateId(null);
      setEditingText('');
      setSelectedTemplates([]);
      setIsAllSelected(false);
    }
  }, [open]);

  // Update isAllSelected when selectedTemplates or templates change
  useEffect(() => {
    if (templates.length === 0) {
      setIsAllSelected(false);
    } else {
      const allTemplateIds = templates.map(template => template.id);
      const isAllSelected = allTemplateIds.every(id => selectedTemplates.includes(id));
      setIsAllSelected(isAllSelected);
    }
  }, [selectedTemplates, templates]);

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  // Handle search submit (Enter key or search button click)
  const handleSearchSubmit = () => {
    loadTemplates(1, searchText);
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    loadTemplates(page, searchText);
  };

  // Handle close
  const handleClose = () => {
    setInputText('');
    setEditingTemplate(null);
    setSearchText('');
    onClose();
  };

  // Handle submit (create or update)
  const handleSubmit = async () => {
    if (!inputText.trim()) {
      enqueueSnackbar('Vui lòng nhập nội dung template', { variant: 'warning' });
      return;
    }

    if (inputText.trim().length > 1000) {
      enqueueSnackbar('Nội dung template không được vượt quá 1000 ký tự', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const isEditing = editingTemplate !== null;
      const url = '/qlvb/incoming-documents/template-ghichu';
      
      let response;
      if (isEditing) {
        // Update existing template
        response = await axiosInstance.put(url, {
          id: editingTemplate.id,
          noidung: inputText.trim()
        });
      } else {
        // Create new template
        response = await axiosInstance.post(url, {
          noidung: inputText.trim()
        });
      }

      if (response.data.code === 0) {
        enqueueSnackbar(
          isEditing ? 'Cập nhật template thành công' : 'Thêm template thành công', 
          { variant: 'success' }
        );
        
        // Reset form and reload data
        setInputText('');
        setEditingTemplate(null);
        setShowActionButtons(false);
        // Reload danh sách từ trang đầu tiên
        loadTemplates(1, searchText);
      } else {
        enqueueSnackbar(response.data.message || 'Có lỗi xảy ra', { variant: 'error' });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Có lỗi xảy ra khi lưu template';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit template - start inline editing
  const handleEditTemplate = (template: Template) => {
    setEditingTemplateId(template.id);
    setEditingText(template.noidung);
  };

  // Handle confirm inline edit
  const handleConfirmInlineEdit = async () => {
    if (!editingTemplateId || !editingText.trim()) {
      return;
    }

    if (editingText.trim().length > 1000) {
      enqueueSnackbar('Nội dung template không được vượt quá 1000 ký tự', { variant: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const response = await axiosInstance.put('/qlvb/incoming-documents/template-ghichu', {
        id: editingTemplateId,
        noidung: editingText.trim()
      });

      if (response.data.code === 0) {
        enqueueSnackbar('Cập nhật template thành công', { variant: 'success' });
        setEditingTemplateId(null);
        setEditingText('');
        loadTemplates(1, searchText);
      } else {
        enqueueSnackbar(response.data.message || 'Có lỗi xảy ra', { variant: 'error' });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật template';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel inline edit
  const handleCancelInlineEdit = () => {
    setEditingTemplateId(null);
    setEditingText('');
  };

  // Handle delete template (single or multiple)
  const handleDeleteTemplate = async (templateIdOrIds: number | number[]) => {
    try {
      const isMultiple = Array.isArray(templateIdOrIds);
      const ids = isMultiple ? templateIdOrIds : [templateIdOrIds];
      
      const response = await axiosInstance.delete('/qlvb/incoming-documents/template-ghichu', {
        data: { ids: ids }
      });

      if (response.data.code === 0) {
        const message = isMultiple 
          ? `Đã xóa ${ids.length} template thành công` 
          : 'Xóa template thành công';
        enqueueSnackbar(message, { variant: 'success' });
        
        // Remove deleted templates from selected list
        if (isMultiple) {
          setSelectedTemplates(prev => prev.filter(id => !ids.includes(id)));
          setIsAllSelected(false);
        }
        
        loadTemplates(pagination.page, searchText);
      } else {
        enqueueSnackbar(response.data.message || 'Có lỗi khi xóa template', { variant: 'error' });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Có lỗi khi xóa template';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingTemplate(null);
    setInputText('');
    setShowActionButtons(false);
  };

  // Handle template click to select
  const handleTemplateClick = (template: Template) => {
    if (onTemplateSelect) {
      onTemplateSelect(template.noidung);
    }
  };

  // Handle input focus - show action buttons (only when not editing)
  const handleInputFocus = () => {
    if (!editingTemplate && !editingTemplateId) {
      setShowActionButtons(true);
    }
  };

  // Handle input blur - hide action buttons if no text
  const handleInputBlur = () => {
    if (!inputText.trim()) {
      setShowActionButtons(false);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    if (e.target.value.trim() && !editingTemplate && !editingTemplateId) {
      setShowActionButtons(true);
    } else {
      setShowActionButtons(false);
    }
  };

  // Handle confirm (check icon) - submit form
  const handleConfirm = () => {
    handleSubmit();
  };

  // Handle cancel (clear icon) - clear text
  const handleCancel = () => {
    setInputText('');
    setShowActionButtons(false);
    setEditingTemplate(null);
  };

  // Handle template selection
  const handleTemplateSelect = (templateId: number) => {
    setSelectedTemplates(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // Handle select all templates
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedTemplates([]);
      setIsAllSelected(false);
    } else {
      const allTemplateIds = templates.map(template => template.id);
      setSelectedTemplates(allTemplateIds);
      setIsAllSelected(true);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedTemplates.length === 0) {
      enqueueSnackbar('Vui lòng chọn ít nhất một template để xóa', { variant: 'warning' });
      return;
    }

    await handleDeleteTemplate(selectedTemplates);
    setSelectedTemplates([]);
    setIsAllSelected(false);
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#086428',
        color: 'white',
        fontWeight: 500,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '51px',
        padding: '12px 16px',
        boxSizing: 'border-box'
      }}>
        Ý kiến mẫu
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Grid bên trái - Form input */}
          <Grid item xs={6} sx={{ 
            p: 3, 
            borderRight: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              fontWeight: 'bold', 
              color: '#086428',
              borderBottom: '2px solid #086428',
              pb: 1
            }}>
              {editingTemplate ? 'Chỉnh sửa template' : 'Thêm ý kiến mới'}
            </Typography>
            
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={5}
              placeholder="Nhập nội dung template..."
              value={inputText}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              InputProps={{
                endAdornment: showActionButtons && inputText.trim() && !editingTemplate && !editingTemplateId && (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={handleConfirm}
                        disabled={submitting || inputText.length > 1000}
                        sx={{
                          color: '#4caf50',
                          '&:hover': {
                            backgroundColor: '#e8f5e8'
                          },
                          '&:disabled': {
                            color: '#ccc'
                          }
                        }}
                      >
                        <CheckIcon sx={{ fontSize: '18px' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancel}
                        sx={{
                          color: '#f44336',
                          '&:hover': {
                            backgroundColor: '#ffebee'
                          }
                        }}
                      >
                        <ClearIcon sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Box>
                  </InputAdornment>
                )
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  fontSize: '14px',
                  height: 'auto',
                  p: 1
                }
              }}
            />

            {editingTemplate && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={handleCancelEdit}
                  variant="outlined"
                  sx={{ fontSize: '12px' }}
                >
                  Hủy chỉnh sửa
                </Button>
              </Box>
            )}
          </Grid>

          {/* Grid bên phải - Danh sách ý kiến */}
          <Grid item xs={6} sx={{ 
            p: 2,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" sx={{ 
              mb: 2, 
              fontWeight: 'bold', 
              color: '#086428',
              borderBottom: '2px solid #086428',
              pb: 1
            }}>
              Danh sách template
            </Typography>

            {/* Search box */}
            <TextField
              fullWidth
              size="small"
              placeholder="Tìm kiếm template..."
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666', fontSize: '18px' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleSearchSubmit}
                      sx={{ p: 0.5 }}
                    >
                      <SearchIcon sx={{ color: '#086428', fontSize: '18px' }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            {/* Select all checkbox */}
            {templates.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    size="small"
                    sx={{ 
                      color: '#086428',
                      '&.Mui-checked': {
                        color: '#086428'
                      }
                    }}
                  />
                }
                label="Chọn tất cả"
                sx={{ 
                  mb: 1,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '13px',
                    color: '#666'
                  }
                }}
              />
            )}
            
            <Box sx={{ 
              flex: 1,
              overflow: 'auto',
              maxHeight: '350px'
            }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : templates.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: '#666' }}>
                  <Typography variant="body2">
                    {searchText ? 'Không tìm thấy template nào' : 'Chưa có template nào'}
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {templates.map((template, index) => (
                    <React.Fragment key={template.id}>
                    <ListItem 
                      sx={{ 
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        p: 0
                      }}
                    >
                        <Checkbox
                          checked={selectedTemplates.includes(template.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleTemplateSelect(template.id);
                          }}
                          size="small"
                          sx={{ 
                            color: '#086428',
                            '&.Mui-checked': {
                              color: '#086428'
                            },
                            mr: 1
                          }}
                        />
                        <ListItemText
                          sx={{
                            my: 0
                          }}
                          primary={
                            <Box>
                              {editingTemplateId === template.id ? (
                                // Inline editing mode
                                <Box sx={{ borderBottom: '1px solid #e0e0e0', p: 1 }}>
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={1}
                                    maxRows={3}
                                    value={editingText}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    placeholder="Nhập nội dung template..."
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton
                                              size="small"
                                              onClick={handleConfirmInlineEdit}
                                              disabled={submitting || editingText.length > 1000}
                                              sx={{
                                                color: '#4caf50',
                                                '&:hover': {
                                                  backgroundColor: '#e8f5e8'
                                                },
                                                '&:disabled': {
                                                  color: '#ccc'
                                                }
                                              }}
                                            >
                                              <CheckIcon sx={{ fontSize: '16px' }} />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              onClick={handleCancelInlineEdit}
                                              sx={{
                                                color: '#f44336',
                                                '&:hover': {
                                                  backgroundColor: '#ffebee'
                                                }
                                              }}
                                            >
                                              <ClearIcon sx={{ fontSize: '16px' }} />
                                            </IconButton>
                                          </Box>
                                        </InputAdornment>
                                      )
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        fontSize: '13px',
                                        height: 'auto',
                                        p: 1
                                      }
                                    }}
                                  />
                                </Box>
                              ) : (
                                // Normal display mode
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'flex-start',  
                                    borderBottom: '1px solid #e0e0e0', 
                                    p: 1,
                                    '&:hover': {
                                      backgroundColor: '#f5f5f5'
                                    }
                                  }}
                                  onClick={() => onTemplateSelect && editingTemplateId !== template.id && handleTemplateClick(template)}
                                >
                                  <Typography variant="body2" sx={{ 
                                    lineHeight: 1.5,
                                    fontSize: '13px',
                                    flex: 1
                                  }}>
                                    {template.noidung}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditTemplate(template);
                                      }}
                                      sx={{ p: 0.5 }}
                                    >
                                      <EditIcon sx={{ fontSize: '16px', color: '#086428' }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteTemplate(template.id);
                                      }}
                                      sx={{ p: 0.5 }}
                                    >
                                      <DeleteIcon sx={{ fontSize: '16px', color: '#f44336' }} />
                                    </IconButton>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>

            {/* Pagination */}
            {/* {pagination.total_pages > 1 && ( */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={pagination.total_pages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  size="small"
                  color="primary"
                />
              </Box>
            {/* )} */}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        borderTop: 1, 
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
        gap: 1
      }}>
        {/* Left side - Selected count and delete button */}
        {selectedTemplates.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Đã chọn: {selectedTemplates.length} template
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={handleBulkDelete}
              sx={{ 
                fontSize: '13px',
                '&:hover': {
                  backgroundColor: '#ffebee'
                }
              }}
            >
              Xóa đã chọn
            </Button>
          </Box>
        )}
        
        {/* Right side - Close button */}
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            fontSize: '13px',
            borderColor: '#ccc',
            color: '#666',
            '&:hover': {
              borderColor: '#999',
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          Hủy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default YKienMauPopup;
